import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { DiagnosisType } from '../types';

interface LocationState {
  type: DiagnosisType;
  horizontalScore: number;
  verticalScore: number;
}

const DiagnosisResult: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!state || !state.type) {
    navigate('/');
    return null;
  }

  const { type, horizontalScore, verticalScore } = state;

  const xMin = -16;
  const xMax = 16;
  const yMin = -16;
  const yMax = 16;

  const userData = [
    { x: horizontalScore, y: verticalScore },
  ];

  const quadrantTypes = [
    { name: '過保護タイプ', x1: 0, x2: xMax, y1: 0, y2: yMax, color: 'rgba(255, 0, 0, 0.1)' },
    { name: '厳格タイプ', x1: xMin, x2: 0, y1: 0, y2: yMax, color: 'rgba(0, 0, 255, 0.1)' },
    { name: '放任タイプ', x1: xMin, x2: 0, y1: yMin, y2: 0, color: 'rgba(0, 255, 0, 0.1)' },
    { name: '言いなりタイプ', x1: 0, x2: xMax, y1: yMin, y2: 0, color: 'rgba(255, 255, 0, 0.1)' },
  ];

  const sudachiType = { name: 'スダチタイプ', x1: -2, x2: 2, y1: -2, y2: 2, color: 'rgba(128, 0, 128, 0.1)' };

  return (
    <div className="container bg-white rounded-lg shadow-lg p-8">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-600">子育てタイプ診断</h1>
      <h2 className="flex items-center text-left text-2xl font-bold text-gray-500">
        <span className="block w-1 h-8 bg-gray-400 mr-4"></span>
        診断結果
      </h2>
      <h2 className="text-3xl font-bold mt-2 mb-6 text-center text-sky-600">{type.typeName}</h2>
      <div className="flex flex-col md:flex-row items-center">
        <div className="bg-white rounded-lg p-6 mb-8 w-full md:w-1/2">
          <div className={`flex ${type.imageUrl.length === 1 ? 'justify-center' : 'justify-between'} items-center mb-6`}>
            {type.imageUrl.map((url, index) => (
              <img 
                key={index} 
                src={url} 
                alt={`${type.typeName} ${index + 1}`} 
                className={`rounded-lg ${type.imageUrl.length === 1 ? 'w-1/2' : 'w-[48%]'}`}
              />
            ))}
          </div>
          <p className="text-lg text-gray-700">{type.description}</p>
        </div>
        <div className="w-full md:w-1/2">
          <h1 className="flex items-center text-left text-2xl font-bold text-gray-500">
            <span className="block w-1 h-8 bg-gray-400 mr-4"></span>
            子育てタイプグラフ
          </h1>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart
              margin={{ top: 20, right: 20, bottom: 40, left: 20 }}
            >
              <XAxis
                type="number"
                dataKey="x"
                domain={[xMin, xMax]}
                tick={false}
                axisLine={false}
              >
                <Label
                  value="子どもと過ごす時間の長さ"
                  offset={0}
                  position="bottom"
                />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                domain={[yMin, yMax]}
                tick={false}
                axisLine={false}
              >
                <Label
                  value="子どもの考えを優先 ← → 親の考えを優先"
                  angle={-90}
                  position="left"
                  style={{ textAnchor: 'middle' }}
                />
              </YAxis>
              <Legend verticalAlign="top" height={36} />

              {quadrantTypes.map((qt, index) => (
                <ReferenceArea
                  key={`quadrant-${index}`}
                  x1={qt.x1}
                  x2={qt.x2}
                  y1={qt.y1}
                  y2={qt.y2}
                  fill={qt.color}
                  fillOpacity={1}
                  label={{
                    value: qt.name,
                    position: 'center',
                    fill: '#333',
                    fontSize: 12,
                  }}
                />
              ))}

              <ReferenceArea
                x1={sudachiType.x1}
                x2={sudachiType.x2}
                y1={sudachiType.y1}
                y2={sudachiType.y2}
                fill={sudachiType.color}
                fillOpacity={1}
                label={{
                  value: sudachiType.name,
                  position: 'center',
                  fill: '#333',
                  fontSize: 12,
                }}
              />

              <ReferenceLine x={0} stroke="gray" strokeWidth={1} />
              <ReferenceLine y={0} stroke="gray" strokeWidth={1} />

              <Scatter name="あなたの位置" data={userData} fill="#FF5733" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={() => navigate('/')}
          className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300"
        >
          再診断する
        </button>
      </div>
    </div>
  );
};

export default DiagnosisResult;